/**
 * AvatarManager - Reference-counted blob URL management
 * 
 * Responsibilities:
 * 1. Deduplicate blob URL creation (one URL per userId)
 * 2. Reference counting (track component usage)
 * 3. Automatic cleanup (revoke when refCount = 0)
 * 4. Cache invalidation (on avatar updates)
 * 5. Memory management (automatic garbage collection)
 */

import logger from './logger';
import indexedDBService from '../services/indexedDB';

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * @typedef {Object} CachedAvatar
 * @property {string} blobUrl - The blob URL for the avatar
 * @property {number} refCount - Number of active references
 * @property {number} timestamp - When this was last accessed
 * @property {string} mxcUrl - The Matrix Content URL (for cache validation)
 */

/**
 * @typedef {Object} AvatarDescriptor
 * @property {string} userId - Matrix user ID
 * @property {string} url - Blob URL or data URL
 */

// ============================================================================
// Avatar Manager Class
// ============================================================================

class AvatarManager {
  constructor() {
    // userId -> CachedAvatar
    this.cache = new Map();
    
    // userId -> Promise (for deduplicating in-flight requests)
    this.inFlightRequests = new Map();
    
    // Configuration
    this.config = {
      // Max age before considering cache entry stale (7 days)
      maxAge: 7 * 24 * 60 * 60 * 1000,

      // Interval for automatic garbage collection (5 minutes)
      gcInterval: 5 * 60 * 1000,

      // Enable debug logging
      debug: false,
      
      // Concurrency control
      maxConcurrent: 10,      // Max concurrent avatar fetches
      requestTimeout: 10000,  // 10 second timeout
      maxRetries: 3,          // Max retries for 429 errors
      maxQueueSize: 100,      // Prevent unbounded queue growth
    };
    
    // Concurrency control state
    this.activeRequests = 0;        // Current in-flight requests
    this.requestQueue = [];         // Pending requests waiting for slot
    
    // Start automatic garbage collection
    this._startGarbageCollection();
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /**
   * Acquire an avatar (increments reference count)
   * Returns cached blob URL if available, otherwise fetches
   * 
   * @param {MatrixClient} client - Matrix client instance
   * @param {string} userId - Matrix user ID (@user:server) or room ID (!room:server)
   * @param {string} displayName - Display name for fallback
   * @param {number} size - Avatar size in pixels
   * @param {string} [entityType] - 'user' or 'room' (auto-detected from ID format if omitted)
   * @returns {Promise<string>} Blob URL or data URL
   */
  async acquire(client, userId, displayName, size = 96, entityType) {
    // Auto-detect entity type from ID format if not provided
    if (!entityType) {
      entityType = userId.startsWith('!') ? 'room' : 'user';
    }

    // Step 1: Check memory cache
    const cached = this.cache.get(userId);
    if (cached) {
      cached.refCount++;
      cached.timestamp = Date.now();
      return cached.blobUrl;
    }

    // Step 2: Deduplicate in-flight requests with recursive retry pattern
    // This prevents TOCTOU race conditions where multiple waiters could
    // overwrite the in-flight promise Map entry after cache invalidation.
    //
    // Pattern:
    // - First caller: creates promise and fetches
    // - Subsequent callers: wait for existing promise, then recursively retry
    // - This ensures only ONE caller becomes the new fetcher after cache miss
    if (!this.inFlightRequests.has(userId)) {
      // We're the first request - create promise and claim slot
      let fetchResolve, fetchReject;
      const fetchPromise = new Promise((resolve, reject) => {
        fetchResolve = resolve;
        fetchReject = reject;
      });
      this.inFlightRequests.set(userId, fetchPromise);

      // Proceed with fetch (wrapped in try/finally)
      return this._performFetch(client, userId, displayName, size, entityType, fetchResolve, fetchReject);
    } else {
      // Another request is already fetching - wait for it
      const existingPromise = this.inFlightRequests.get(userId);

      try {
        await existingPromise;
      } catch {
        // Ignore errors from other request - we'll re-check cache
      }

      // Re-check cache after waiting
      const cachedAfterWait = this.cache.get(userId);
      if (cachedAfterWait) {
        cachedAfterWait.refCount++;
        cachedAfterWait.timestamp = Date.now();
        return cachedAfterWait.blobUrl;
      }

      // Cache was cleared or fetch failed - RECURSIVELY RETRY
      // This re-enters the function and re-checks has(), preventing race conditions
      // where multiple waiters would overwrite the in-flight promise Map entry
      return this.acquire(client, userId, displayName, size, entityType);
    }
  }

  /**
   * Perform the actual avatar fetch (internal helper)
   * Separated from acquire() to enable clean recursive retry pattern
   * @private
   */
  async _performFetch(client, userId, displayName, size, entityType, fetchResolve, fetchReject) {

    try {
      // Step 4: Wait for available concurrency slot
      const slotResult = await this._waitForSlot(userId, displayName, size);
      if (slotResult?.queueFull) {
        fetchResolve(); // Resolve the in-flight promise for waiters
        return slotResult.fallbackUrl;
      }

      // Step 5: Re-check cache after queue wait (may have been fetched by earlier request)
      const cachedAfterQueue = this.cache.get(userId);
      if (cachedAfterQueue) {
        this._releaseSlot();  // Release the slot we just reserved
        cachedAfterQueue.refCount++;
        cachedAfterQueue.timestamp = Date.now();
        // this._log('acquire', userId, `cache HIT after queue, refCount=${cachedAfterQueue.refCount}`);
        fetchResolve(); // Resolve the in-flight promise for waiters
        return cachedAfterQueue.blobUrl;
      }

      // Step 6: Fetch avatar with concurrency tracking
      const result = await this._fetchAvatarWithConcurrency(client, userId, displayName, size, entityType);
      const { blobUrl, mxcUrl } = result;

      // Add to cache with initial refCount = 1
      this.cache.set(userId, {
        blobUrl,
        mxcUrl,
        refCount: 1,
        timestamp: Date.now(),
        type: entityType,  // Track entity type for debugging/stats
      });

      logger.debug('Avatar cache: stored in memory', { userId, entityType });
      fetchResolve(); // Resolve the in-flight promise for waiters
      return blobUrl;
    } catch (error) {
      fetchReject(error); // Propagate error to waiters
      throw error;
    } finally {
      this.inFlightRequests.delete(userId);
    }
  }

  /**
   * Release an avatar (decrements reference count)
   * Revokes blob URL if refCount reaches 0
   *
   * @param {string} userId - Matrix user ID or room ID
   */
  release(userId) {
    const cached = this.cache.get(userId);
    if (!cached) {
      return;
    }

    cached.refCount--;

    if (cached.refCount <= 0) {
      this._revoke(userId);
    }
  }

  /**
   * Invalidate cache for a user or room (forces re-fetch on next acquire)
   * Used when avatar changes
   *
   * @param {string} userId - Matrix user ID or room ID
   */
  async invalidate(userId) {
    // Revoke blob URL and remove from memory
    this._revoke(userId);

    // Clear IndexedDB cache
    await indexedDBService.deleteCachedAvatar(userId);

    logger.info(`Avatar cache invalidated for ${userId}`);
  }

  /**
   * Clear all cached avatars (logout)
   */
  clearAll() {
    logger.info(`Clearing ${this.cache.size} cached avatars`);
    
    for (const [userId] of this.cache.entries()) {
      this._revoke(userId);
    }
    
    this.cache.clear();
  }

  /**
   * Get cache statistics (for debugging)
   */
  getStats() {
    const stats = {
      totalCached: this.cache.size,
      totalRefs: 0,
      users: 0,     // NEW: count user avatars
      rooms: 0,     // NEW: count room avatars
      inFlight: this.inFlightRequests.size,
      activeRequests: this.activeRequests,
      queuedRequests: this.requestQueue.length,
      config: {
        maxConcurrent: this.config.maxConcurrent,
        requestTimeout: this.config.requestTimeout,
        maxRetries: this.config.maxRetries,
        maxQueueSize: this.config.maxQueueSize,
      },
      entries: [],
    };

    for (const [userId, cached] of this.cache.entries()) {
      stats.totalRefs += cached.refCount;
      
      // Count by type
      if (cached.type === 'room') {
        stats.rooms++;
      } else {
        stats.users++;
      }
      
      stats.entries.push({
        userId,  // Keep existing field name for backward compatibility
        type: cached.type || 'user',  // Add new field
        refCount: cached.refCount,
        age: Date.now() - cached.timestamp,
      });
    }

    return stats;
  }

  // ==========================================================================
  // Internal Methods
  // ==========================================================================

  /**
   * Revoke blob URL and remove from cache
   * @private
   */
  _revoke(userId) {
    const cached = this.cache.get(userId);
    if (!cached) {
      return;
    }

    if (cached.blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(cached.blobUrl);
    }

    this.cache.delete(userId);
  }

  // ==========================================================================
  // Concurrency Control (Part 1)
  // ==========================================================================

  /**
   * Wait for available concurrency slot
   * @private
   */
  async _waitForSlot(userId, displayName, size) {
    // Check if we need to queue BEFORE reserving slot
    if (this.activeRequests >= this.config.maxConcurrent) {
      // Check queue size limit
      if (this.requestQueue.length >= this.config.maxQueueSize) {
        logger.warn(`Avatar queue full for ${userId}, returning initials`);
        const { generateInitialsAvatar } = await import('./avatar');
        const initialsUrl = generateInitialsAvatar(displayName || userId, size);
        return { queueFull: true, fallbackUrl: initialsUrl };
      }

      // Queue the request
      // this._log('waitForSlot', userId, `queued (${this.requestQueue.length} ahead)`);

      await new Promise((resolve) => {
        this.requestQueue.push({ userId, resolve });
      });
    }
    
    // Reserve slot atomically
    this.activeRequests++;
    // this._log('waitForSlot', userId, `slot reserved, activeRequests=${this.activeRequests}`);
    return null; // No fallback needed
  }

  /**
   * Release concurrency slot and process queue
   * @private
   */
  _releaseSlot() {
    this.activeRequests--;

    // Process next queued request
    if (this.requestQueue.length > 0) {
      const next = this.requestQueue.shift();
      // this._log('releaseSlot', next.userId, `dequeued (${this.requestQueue.length} remaining)`);
      next.resolve();
    }
  }

  /**
   * Fetch avatar with concurrency tracking
   * @private
   */
  async _fetchAvatarWithConcurrency(client, userId, displayName, size, entityType) {
    // Slot already reserved in _waitForSlot - don't increment again
    // this._log('fetch', userId, `starting fetch, activeRequests=${this.activeRequests}, type=${entityType}`);

    try {
      return await this._fetchAvatarWithRetry(client, userId, displayName, size, entityType);
    } finally {
      this._releaseSlot();
    }
  }

  // ==========================================================================
  // Rate Limiting & Retry Logic (Part 2)
  // ==========================================================================

  /**
   * Fetch avatar with retry on 429
   * @private
   */
  async _fetchAvatarWithRetry(client, userId, displayName, size, entityType) {
    let lastError = null;

    for (let attempt = 0; attempt < this.config.maxRetries; attempt++) {
      try {
        // Add timeout to fetch
        return await this._fetchAvatarWithTimeout(client, userId, displayName, size, entityType);
      } catch (error) {
        lastError = error;

        // Check if it's a 429 rate limit error
        if (error.status === 429) {
          const retryAfter = this._parseRetryAfter(error.headers);
          const baseWaitTime = retryAfter || Math.pow(2, attempt) * 1000; // Exponential backoff
          const jitter = Math.random() * 1000; // 0-1000ms random jitter
          const waitTime = baseWaitTime + jitter;

          logger.warn(`Avatar fetch rate limited for ${userId}, waiting ${Math.round(waitTime)}ms (attempt ${attempt + 1}/${this.config.maxRetries})`);

          await this._sleep(waitTime);
          continue; // Retry
        }

        // Other errors: don't retry
        throw error;
      }
    }

    // All retries exhausted - return initials fallback instead of throwing
    logger.error(`Avatar fetch failed after ${this.config.maxRetries} retries for ${userId}:`, lastError);
    const { generateInitialsAvatar } = await import('./avatar');
    const initialsUrl = generateInitialsAvatar(displayName || userId, size);
    return { blobUrl: initialsUrl, mxcUrl: null };
  }

  /**
   * Parse Retry-After header (seconds or HTTP date)
   * @private
   */
  _parseRetryAfter(headers) {
    if (!headers) return null;

    const retryAfter = headers.get('Retry-After') || headers.get('retry-after');
    if (!retryAfter) return null;

    // Check if it's a number (seconds)
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return seconds * 1000; // Convert to milliseconds
    }

    // Try parsing as HTTP date
    try {
      const date = new Date(retryAfter);
      const now = Date.now();
      return Math.max(0, date.getTime() - now);
    } catch {
      return null;
    }
  }

  /**
   * Sleep helper
   * @private
   */
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ==========================================================================
  // Timeout Protection (Part 3)
  // ==========================================================================

  /**
   * Fetch avatar with timeout
   * @private
   */
  async _fetchAvatarWithTimeout(client, userId, displayName, size, entityType) {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      this.config.requestTimeout,
    );

    try {
      // Pass abort signal to _fetchAvatar
      return await this._fetchAvatar(client, userId, displayName, size, controller.signal, entityType);
    } catch (error) {
      // Re-throw 429 errors immediately for retry logic
      if (error.status === 429) {
        throw error;
      }

      // Check if it was a timeout
      if (error.name === 'AbortError') {
        logger.warn(`Avatar fetch timeout for ${userId} after ${this.config.requestTimeout}ms`);
        const { generateInitialsAvatar } = await import('./avatar');
        const initialsUrl = generateInitialsAvatar(displayName || userId, size);
        return { blobUrl: initialsUrl, mxcUrl: null };
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // ==========================================================================
  // Core Fetch Logic
  // ==========================================================================

  /**
   * Fetch avatar with IndexedDB caching
   * @private
   */
  async _fetchAvatar(client, userId, displayName, size, signal = null, entityType = 'user') {
    // Import here to avoid circular dependency
    const { generateInitialsAvatar } = await import('./avatar');
    
    try {
      let mxcUrl;
      
      // Step 1: Get MXC URL based on entity type
      if (entityType === 'room') {
        // For rooms: get MXC URL from room state
        const room = client.getRoom(userId);
        if (!room) {
          logger.debug('Avatar fetch: room not found', { userId });
          const initialsUrl = generateInitialsAvatar(displayName || userId, size);
          return { blobUrl: initialsUrl, mxcUrl: null };
        }
        mxcUrl = room.getMxcAvatarUrl();
        
      } else {
        // For users: existing logic
        const user = client.getUser(userId);
        mxcUrl = user?.avatarUrl;

        // Step 1b: If missing, fetch from server (handles disablePresence: true)
        if (!mxcUrl) {
          try {
            const profile = await client.getProfileInfo(userId);
            mxcUrl = profile.avatar_url;
          } catch (error) {
            logger.debug('Avatar fetch: could not fetch profile', { userId, error: error.message });
          }
        }
      }

      // No avatar available - return initials
      if (!mxcUrl) {
        const initialsUrl = generateInitialsAvatar(displayName || userId, size);
        return { blobUrl: initialsUrl, mxcUrl: null };
      }

      // Step 2: Convert MXC to HTTP URL
      const httpUrl = client.mxcUrlToHttp(
        mxcUrl,
        size,
        size,
        'crop',
        false,  // allowDirectLinks
        true,   // allowRedirects
        true,    // useAuthentication
      );

      if (!httpUrl) {
        const initialsUrl = generateInitialsAvatar(displayName || userId, size);
        return { blobUrl: initialsUrl, mxcUrl: null };
      }

      // Step 3: Check IndexedDB cache
      // CRITICAL FIX: Wrap IndexedDB call in try-catch to make errors non-blocking
      // IndexedDB failures should not prevent server fetch fallback
      //
      // NOTE: IndexedDB key is userId (includes @ or ! prefix for users/rooms)
      // Matrix IDs are globally unique by format, so no collision risk between entities
      try {
        const cached = await indexedDBService.getCachedAvatar(userId, mxcUrl);

        // CRITICAL FIX: Validate cached MXC URL matches current MXC URL
        // If user changed avatar, cached data is stale and should be ignored
        if (cached && cached.blobData && cached.mxcUrl === mxcUrl) {
          // Create blob from cached data
          const blob = new Blob([cached.blobData], { type: cached.mimeType });
          const blobUrl = URL.createObjectURL(blob);

          logger.debug('Avatar cache: IndexedDB hit', { userId, size: blob.size });
          return { blobUrl, mxcUrl };
        }
      } catch (idbError) {
        // IndexedDB read error should be non-blocking - log and continue to server fetch
        logger.warn(`IndexedDB read error for ${userId}, falling back to server:`, idbError);
      }

      // Step 4: Fetch from server
      logger.debug('Avatar fetch: starting network request', { userId, size });
      
      const response = await fetch(httpUrl, {
        headers: {
          'Authorization': `Bearer ${client.getAccessToken()}`,
        },
        signal,  // Add abort signal for timeout support
      });

      // Check for 429 rate limit and throw with headers
      if (response.status === 429) {
        const error = new Error('Rate limited');
        error.status = 429;
        error.headers = response.headers;
        throw error;
      }

      if (!response.ok) {
        logger.warn(`Failed to fetch avatar for ${userId}: ${response.status}`);
        const initialsUrl = generateInitialsAvatar(displayName || userId, size);
        return { blobUrl: initialsUrl, mxcUrl: null };
      }

      // Step 5: Convert to blob and cache
      const blob = await response.blob();
      const mimeType = blob.type || 'image/png';
      const arrayBuffer = await blob.arrayBuffer();

      // Cache in IndexedDB (async, non-blocking)
      Promise.resolve(indexedDBService.cacheAvatar(userId, mxcUrl, arrayBuffer, mimeType))
        .then(() => {
          logger.debug('Avatar cache: write to IndexedDB complete', { userId, size: arrayBuffer.byteLength });
        })
        .catch(err => {
          logger.warn('Failed to cache avatar:', err);
        });

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      this._log('fetch', userId, `fetched from server (${blob.size} bytes)`);
      return { blobUrl, mxcUrl };

    } catch (error) {
      // Re-throw 429 errors so retry logic can handle them
      if (error.status === 429) {
        throw error;
      }

      logger.error(`Error fetching avatar for ${userId}:`, error);
      const { generateInitialsAvatar } = await import('./avatar');
      const initialsUrl = generateInitialsAvatar(displayName || userId, size);
      return { blobUrl: initialsUrl, mxcUrl: null };
    }
  }

  /**
   * Start automatic garbage collection
   * @private
   */
  _startGarbageCollection() {
    this._gcTimer = setInterval(() => {
      this._garbageCollect();
    }, this.config.gcInterval);
  }

  /**
   * Stop automatic garbage collection
   * @private
   */
  _stopGarbageCollection() {
    if (this._gcTimer) {
      clearInterval(this._gcTimer);
      this._gcTimer = null;
    }
  }

  /**
   * Garbage collection - remove stale entries with refCount = 0
   * @private
   */
  _garbageCollect() {
    const now = Date.now();
    let collected = 0;

    for (const [userId, cached] of this.cache.entries()) {
      const age = now - cached.timestamp;
      
      // Remove entries that are:
      // 1. Not referenced (refCount = 0)
      // 2. Older than maxAge
      // 3. No pending in-flight or queued requests
      if (cached.refCount === 0 && age > this.config.maxAge) {
        // Don't revoke if there's pending activity for this user
        const hasInflight = this.inFlightRequests.has(userId);
        const hasQueued = this.requestQueue.some(item => item.userId === userId);
        
        if (!hasInflight && !hasQueued) {
          this._revoke(userId);
          collected++;
        } else {
          this._log('gc', userId, 'skipped (has pending requests)');
        }
      }
    }

    if (collected > 0) {
      logger.debug(`Garbage collected ${collected} stale avatars`);
    }
  }

  /**
   * Debug logging
   * @private
   */
  _log(method, userId, message) {
    if (this.config.debug) {
      console.log(`[AvatarManager.${method}] ${userId}: ${message}`);
    }
  }

  /**
   * Cleanup on application shutdown
   * Stops garbage collection timer and clears all avatars
   * Call this on logout to prevent memory leaks
   */
  cleanup() {
    logger.info('AvatarManager: Starting cleanup');
    this._stopGarbageCollection();
    this.clearAll();
    logger.info('AvatarManager: Cleanup complete');
  }
}

// ============================================================================
// Singleton Instance & Exports
// ============================================================================

/** @type {AvatarManager} */
export const avatarManager = new AvatarManager();

// Convenience exports
export const acquireAvatar = (client, userId, displayName, size, entityType) =>
  avatarManager.acquire(client, userId, displayName, size, entityType);

export const releaseAvatar = (userId) =>
  avatarManager.release(userId);

export const invalidateAvatar = (userId) =>
  avatarManager.invalidate(userId);

export const clearAllAvatars = () =>
  avatarManager.clearAll();

export const getAvatarStats = () =>
  avatarManager.getStats();

// Enable debug mode in development
if (process.env.NODE_ENV === 'development') {
  avatarManager.config.debug = true;
  
  // Add to window for debugging
  if (typeof window !== 'undefined') {
    window.avatarManager = avatarManager;
  }
}

export default avatarManager;
